import { toJpeg, toPng } from 'html-to-image';

const isAppleWebKit = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iP(hone|ad|od)/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS || isSafari;
};

// Safari/iOS quirks with html-to-image:
//   1. The first render often produces a blank/partial image because embedded
//      images/fonts aren't decoded yet — the accepted workaround is to call
//      toPng multiple times until the output stabilises.
//   2. iOS Safari caps canvas area at ~16.7M px; a large board × pixelRatio 4
//      silently produces an empty canvas. Clamp the ratio on WebKit.
//   3. Programmatic <a download> only fires reliably when the anchor is in
//      the DOM at click time.
export async function renderNodeToPng(
  node: HTMLElement,
  opts: {
    backgroundColor?: string;
    pixelRatio?: number;
    format?: 'png' | 'jpeg';
    quality?: number;
  } = {},
): Promise<string> {
  if (document?.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // ignore
    }
  }

  const apple = isAppleWebKit();
  const rect = node.getBoundingClientRect();
  const requested = opts.pixelRatio ?? 4;
  // iOS: keep total pixels under the 16M canvas limit.
  const maxDim = apple ? Math.floor(Math.sqrt(16_000_000)) : Infinity;
  const largest = Math.max(rect.width, rect.height, 1);
  const safeRatio = Math.max(1, Math.min(requested, maxDim / largest));

  const format = opts.format ?? 'png';
  const options = {
    cacheBust: true,
    pixelRatio: safeRatio,
    backgroundColor: opts.backgroundColor,
    quality: opts.quality,
  };

  let dataUrl = '';
  const attempts = apple ? 3 : 1;
  const render = format === 'jpeg' ? toJpeg : toPng;
  for (let i = 0; i < attempts; i++) {
    dataUrl = await render(node, options);
    if (dataUrl.length > 100_000) break;
  }
  return dataUrl;
}

export function triggerDownload(dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => link.remove(), 0);
}
